/**
 * Core type definitions for the Grossman-Miller Simulator
 * Based on Grossman, S.J. and Miller, M.H. (1988), "Liquidity and Market Structure"
 */

// Agent identification
export type AgentId = 'MM' | 'LT1' | 'LT2';

// Time steps in the three-date model
export type TimeStep = 1 | 2 | 3;

/**
 * Model parameters as specified in PRD FR-1 and FR-3
 */
export interface ModelParams {
  // Core parameters (FR-1)
  n: number;           // Number of market makers (integer >= 1)
  i: number;           // Order imbalance (LT1's initial endowment, positive = seller)
  mu: number;          // Expected terminal value (real > 0)
  sigma: number;       // Volatility - std dev of each shock (real > 0)
  a: number;           // Risk aversion coefficient (CARA parameter, real > 0)
  W0: number;          // Initial cash for each agent (real >= 0, display only)

  // Advanced parameters (FR-3)
  eta: number;         // Trading fee per share (real >= 0)
  c: number;           // Participation cost for MM entry (real >= 0)
}

/**
 * Random shocks drawn for a simulation run
 */
export interface Shocks {
  epsilon2: number;    // Revealed between t=1 and t=2, ~ N(0, sigma^2)
  epsilon3: number;    // Revealed between t=2 and t=3, ~ N(0, sigma^2)
}

/**
 * Equilibrium computation results
 */
export interface EquilibriumResult {
  // Prices
  S1: number;          // Transaction price at t=1
  S2: number;          // Transaction price at t=2 (= mu + epsilon2)
  S3: number;          // Terminal value (= mu + epsilon2 + epsilon3)

  // Holdings at end of t=1
  q1_MM: number;       // Each MM's holding after t=1 trading
  q1_LT1: number;      // LT1's holding after t=1 trading

  // Liquidity metrics
  liquidityDiscount: number;      // mu - S1 (total)
  riskComponent: number;          // (a * sigma^2 * i) / (n + 1)
  feeComponent: number;           // (2 * n / (n + 1)) * eta

  // Immediacy (PRD FR-10)
  immediacy: number;              // n / (n + 1) - fraction of trade completed at t=1

  // Price autocorrelation (PRD FR-27)
  autocorrelation: number;        // q = -t / (1 + t)
  autocorrelationT: number;       // t parameter in autocorrelation formula

  // Endogenous entry (when c > 0)
  endogenousN?: number;           // Solved equilibrium number of MMs
}

/**
 * Agent state at a specific time
 */
export interface AgentState {
  inventory: number;              // Units of risky asset held
  cash: number;                   // Cash position
  cumulativePnL: number;          // cash - W0
  riskExposure: number;           // Variance of next-period wealth change
  tradeThisPeriod: number;        // Delta from previous period (for display)
}

/**
 * Full simulation state (PRD FR-30)
 */
export interface SimulationState {
  // Input parameters
  params: ModelParams;

  // Random draws (fixed for a given run)
  shocks: Shocks;

  // Computed equilibrium values
  equilibrium: EquilibriumResult;

  // Agent states by time (indexed 1, 2, 3)
  agents: {
    MM: Record<TimeStep, AgentState>;
    LT1: Record<TimeStep, AgentState>;
    LT2: Record<TimeStep, AgentState>;
  };

  // Current view position
  currentTime: TimeStep;

  // Mode flags
  deterministicMode: boolean;
  seed?: string;
}

/**
 * Simulation options for running the model
 */
export interface SimulationOptions {
  seed?: string;                   // RNG seed for reproducibility
  deterministicPath?: boolean;     // Use fixed epsilon values (epsilon2 = 0, epsilon3 = 0)
  fixedEpsilon2?: number;          // Override epsilon2 value
  fixedEpsilon3?: number;          // Override epsilon3 value
}

/**
 * Default parameter values (PRD FR-1 and FR-2)
 */
export const DEFAULT_PARAMS: ModelParams = {
  n: 5,              // 5 market makers
  i: 10,             // LT1 selling 10 units
  mu: 100,           // Expected terminal value = 100
  sigma: 2,          // Volatility = 2 (variance = 4)
  a: 0.01,           // Risk aversion coefficient
  W0: 10000,         // Initial cash per agent
  eta: 0,            // No trading fees
  c: 0,              // No participation cost
};

/**
 * Parameter constraints for validation (PRD FR-4)
 */
export const PARAM_CONSTRAINTS = {
  n: { min: 1, max: 1000, integer: true },
  i: { min: -1000000, max: 1000000 },
  mu: { min: 0.01, max: 1000000 },
  sigma: { min: 0.0001, max: 1000 },
  a: { min: 0.0001, max: 1000 },
  W0: { min: 0, max: 1000000000 },
  eta: { min: 0, max: 10000 },
  c: { min: 0, max: 1000000 },
} as const;

/**
 * Validation result for parameter checking
 */
export interface ValidationResult {
  valid: boolean;
  errors: Partial<Record<keyof ModelParams, string>>;
}
